import axios from 'axios';
import { authUtils } from '../../auth/utils/auth.utils';
import axiosInstance from "../../../middlewares/axiosInstance";
import { staffService } from '../../business-admin/services/staff.service';
import { authService } from '../../../services/auth.service';

interface PaymentResponse {
  success: boolean;
  data: {
    payment_url: string;
    track_id: string;
    success: boolean;
  };
}

interface CreateSubscriptionRequest {
  business_id: string;
  plan_type: 'BASIC' | 'PRO' | 'ENTERPRISE';
  billing_cycle: 'MONTHLY' | 'YEARLY';
  payment_method: 'CREDIT_CARD' | 'BANK_TRANSFER' | 'BENEFIT';
  auto_renew?: boolean;
  track_id: string;
}

interface CreateSubscriptionResponse {
  success: boolean;
  data?: {
    subscription_id: string;
    status: string;
  };
  error?: string;
}

interface UpdateStatusResponse {
  success: boolean;
  data?: {
    status: string;
  };
  error?: string;
}

export const initiateVPayPayment = async (
  userId: string,
  amount: number
): Promise<PaymentResponse> => {
  try {
    const returnUrl = `${window.location.origin}/subscription/confirmation`;
    
    const response = await axiosInstance.post<PaymentResponse>(
      "/public/subscription-payments/initiate-vpay",
      {
        user_id: userId,
        amount: amount,
        return_url: returnUrl
      }
    );
    
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Payment initiation failed');
    }
    throw error;
  }
};

export const createSubscription = async (
  subscriptionData: CreateSubscriptionRequest
): Promise<CreateSubscriptionResponse> => {
  try {
    const response = await axiosInstance.post<CreateSubscriptionResponse>(
      "/public/subscriptions/create",
      subscriptionData
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to create subscription');
    }
    throw error;
  }
};

export const updateSubscriptionStatus = async (
  trackId: string,
  status: 'ACTIVE' | 'CANCELLED'
): Promise<UpdateStatusResponse> => {
  try {
    const response = await axiosInstance.post<UpdateStatusResponse>(
      "/public/subscriptions/update-status",
      {
        track_id: trackId,
        status
      }
    );

    // If subscription is successful and status is ACTIVE, update the user's is_subscribed field
    if (response.data.success && status === 'ACTIVE') {
      try {
        // Get the current user using authService
        const currentUser = await authService.getCurrentUser();
        
        if (currentUser && currentUser.id) {
          // Update the user's is_subscribed field using the staff service
          await staffService.updateStaffMember({
            id: currentUser.id,
            is_subscribed: true
          });
          
          console.log('User subscription status updated successfully');
        } else {
          console.error('Current user not found, could not update subscription status');
        }
      } catch (userUpdateError) {
        console.error('Error updating user subscription status:', userUpdateError);
        // We don't want to fail the entire subscription process if just the user update fails
      }
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to update subscription status');
    }
    throw error;
  }
};

export const updatePaymentStatus = async (
  trackId: string,
  status: 'COMPLETED' | 'FAILED'
): Promise<UpdateStatusResponse> => {
  try {
    const response = await axiosInstance.post<UpdateStatusResponse>(
      "/public/subscription-payments/update-status",
      {
        track_id: trackId,
        status
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Failed to update payment status');
    }
    throw error;
  }
}; 